import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as api from './offlineAPI';
import { getAllDataFromDB } from '../../utils/indexedDBFunctions';

const initialState = {
  isOnline: true,
  pendingTasks: {},
  currentTaskTitle: '',
  isIndexedDBInitialized: false,
  lastSynced: null,
  error: {},
  indexedDBError: false,
  showOfflineWarning: false,
  syncStatus: 'idle',
  modalStage: 'intro',
  offlineFetchStatus: 'idle',
  onlineStatus: 'idle',
};

export const fetchOfflineData = createAsyncThunk(
  'offline/fetchOfflineData',
  async () => {
    const response = await api.fetchOfflineData();
    return response.body;
  }
);

export const checkOnlineStatus = createAsyncThunk(
  'offline/checkOnlineStatus',
  async () => {
    const response = await api.checkOnlineStatus();
    return response.body;
  }
);

export const fetchPendingTasks = createAsyncThunk(
  'offline/fetchPendingTasks',
  async (userId) => {
    const pendingTasksArray = await getAllDataFromDB(userId, 'pendingTasks');
    const pendingTasks = pendingTasksArray.reduce((acc, { payload, ...taskWithoutPayload }) => {
      acc[taskWithoutPayload.taskId] = {
        ...taskWithoutPayload,
        payload: null,
      };
      return acc;
    }, {});

    return pendingTasks;
  }
);

export const offlineSlice = createSlice({
  name: 'offline',
  initialState,
  reducers: {
    setIsOnline: (state, action) => {
      state.isOnline = action.payload;
    },
    setIsIndexedDBInitialized: (state, action) => {
      state.isIndexedDBInitialized = action.payload;
    },
    setLastSynced: (state, action) => {
      state.lastSynced = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setIndexedDBError: (state, action) => {
      state.indexedDBError = action.payload;
    },
    setCurrentTaskTitle: (state, action) => {
      state.currentTaskTitle = action.payload;
    },
    setShowOfflineWarning: (state, action) => {
      state.showOfflineWarning = action.payload;
    },
    updateNodeIdInPendingTasks: (state, action) => {
      const tempNodeId = action.payload.tempNodeId;
      const newNodeId = action.payload.newNodeId;
      for (const key in state.pendingTasks) {
        if (
          Object.prototype.hasOwnProperty.call(state.pendingTasks, key) &&
          state.pendingTasks[key].tempId === tempNodeId &&
          state.pendingTasks[key].nodeId &&
          state.pendingTasks[key].nodeId.startsWith('temp-')
        ) {
          // Create a new object with the updated nodeId
          state.pendingTasks[key] = { ...state.pendingTasks[key], nodeId: newNodeId };
          break;
        }
      }
    },
    updateSectionNodeIdsInPendingTasks: (state, action) => {
      const { updatedSectionTasks } = action.payload;
      updatedSectionTasks.forEach(updatedTask => {
        if (state.pendingTasks[updatedTask.taskId]) {
          state.pendingTasks[updatedTask.taskId] = updatedTask;
        }
      });
    },
    setSyncStatus: (state, action) => {
      state.syncStatus = action.payload;
    },
    setModalStage(state, action) {
      state.modalStage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOfflineData.pending, (state) => {
        state.offlineFetchStatus = 'loading';
      })
      .addCase(fetchOfflineData.fulfilled, (state) => {
        state.offlineFetchStatus = 'idle';
      })
      .addCase(fetchOfflineData.rejected, (state) => {
        state.offlineFetchStatus = 'error';
      })

      .addCase(checkOnlineStatus.pending, (state) => {
        state.onlineStatus = 'loading';
      })
      .addCase(checkOnlineStatus.fulfilled, (state, action) => {
        state.onlineStatus = action.payload.status === 'online' ? 'online' : 'offline';
      })
      .addCase(checkOnlineStatus.rejected, (state) => {
        state.onlineStatus = 'offline';
      })

      .addCase(fetchPendingTasks.fulfilled, (state, action) => {
        state.pendingTasks = action.payload;
      });
  },
});

export const {
  setIsOnline,
  setCurrentTaskTitle,
  setIsIndexedDBInitialized,
  setLastSynced,
  setError,
  setIndexedDBError,
  setShowOfflineWarning,
  updateNodeIdInPendingTasks,
  updateSectionNodeIdsInPendingTasks,
  setSyncStatus,
  setModalStage,
} = offlineSlice.actions;

export const selectIsOnline = (state) => state.offline.isOnline;
export const selectPendingTasks = (state) => state.offline.pendingTasks;
export const selectPendingTaskCount = (state) => Object.keys(state.offline.pendingTasks).length;
export const selectIsIndexedDBInitialized = (state) => state.offline.isIndexedDBInitialized;
export const selectLastSynced = (state) => state.offline.lastSynced;
export const selectError = (state) => state.offline.error;
export const selectIndexedDBError = (state) => state.offline.indexedDBError;
export const selectCurrentTaskTitle = (state) => state.offline.currentTaskTitle;
export const selectShowOfflineWarning = (state) => state.offline.showOfflineWarning;
export const selectSyncStatus = (state) => state.offline.syncStatus;
export const selectModalStage = (state) => state.offline.modalStage;
export const selectOfflineFetchStatus = (state) => state.offline.offlineFetchStatus;
export const selectOnlineStatus = (state) => state.offline.onlineStatus;

export default offlineSlice.reducer;